import { useEffect, useState } from "react";
import { Receipt, Download, FileText, Loader2, Filter, Mail, Send, TrendingUp } from "lucide-react";
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const eur = (v) => (v ?? 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 4 });
const dmy = (d) => (d && d.length >= 10 ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : d || "");
const iso = (d) => d.toISOString().slice(0, 10);

const PRESETS = [
  ["month", "Questo mese"], ["prev", "Mese scorso"], ["30", "Ultimi 30 giorni"], ["90", "Ultimi 90 giorni"], ["custom", "Personalizzato"],
];

function presetRange(p) {
  const t = new Date();
  if (p === "month") return [iso(new Date(t.getFullYear(), t.getMonth(), 1)), iso(t)];
  if (p === "prev") return [iso(new Date(t.getFullYear(), t.getMonth() - 1, 1)), iso(new Date(t.getFullYear(), t.getMonth(), 0))];
  if (p === "30" || p === "90") { const s = new Date(t); s.setDate(s.getDate() - Number(p)); return [iso(s), iso(t)]; }
  return null;
}

export function MetaStatements() {
  const [preset, setPreset] = useState("month");
  const [[start, end], setRange] = useState(() => presetRange("month"));
  const [workspace, setWorkspace] = useState("all");
  const [campaign, setCampaign] = useState("all");
  const [channels, setChannels] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);

  useEffect(() => { api.get("/workspaces").then((r) => setChannels(r.data.items || [])).catch(() => {}); }, []);
  useEffect(() => {
    setCampaign("all");
    api.get("/statements/meta/campaigns", { params: { workspace } }).then((r) => setCampaigns(r.data || [])).catch(() => setCampaigns([]));
  }, [workspace]);

  const params = { start, end, workspace, ...(campaign !== "all" ? { campaign_id: campaign } : {}) };

  useEffect(() => {
    if (!start || !end) return;
    setLoading(true);
    api.get("/statements/meta", { params }).then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Estratto conto non disponibile"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [start, end, workspace, campaign]);

  const choosePreset = (p) => { setPreset(p); const r = presetRange(p); if (r) setRange(r); };

  const exportFile = async (format) => {
    setExporting(format);
    try {
      const res = await api.get("/statements/meta/export", { params: { ...params, format }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `estratto_conto_meta_${start}_${end}.${format}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Esportazione non riuscita"); } finally { setExporting(null); }
  };

  return (
    <section className="space-y-5" data-testid="meta-statements">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2"><Receipt className="h-5 w-5 text-[#1E4FD8]" /> Estratti conto addebiti Meta</h2>
          <p className="text-sm text-slate-500 mt-1">Costi reali addebitati da Meta, ripartiti per canale, campagna e giorno.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" disabled={!!exporting || loading} onClick={() => exportFile("csv")} data-testid="statement-export-csv" className="bg-white border-slate-200 text-slate-700">
            {exporting === "csv" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} CSV
          </Button>
          <Button disabled={!!exporting || loading} onClick={() => exportFile("pdf")} data-testid="statement-export-pdf" className="bg-[#1E4FD8] hover:bg-[#1a44bb] text-white">
            {exporting === "pdf" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />} PDF
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 bg-white border border-slate-200 rounded-2xl p-4" data-testid="statement-filters">
        <div className="space-y-1">
          <span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium flex items-center gap-1"><Filter className="h-3 w-3" /> Periodo</span>
          <Select value={preset} onValueChange={choosePreset}>
            <SelectTrigger className="w-[170px] bg-white border-slate-200" data-testid="statement-preset"><SelectValue /></SelectTrigger>
            <SelectContent>{PRESETS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1"><span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Dal</span>
          <Input type="date" value={start} max={end} onChange={(e) => { setPreset("custom"); setRange([e.target.value, end]); }} data-testid="statement-start" className="w-[150px] bg-white border-slate-200" /></div>
        <div className="space-y-1"><span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Al</span>
          <Input type="date" value={end} min={start} onChange={(e) => { setPreset("custom"); setRange([start, e.target.value]); }} data-testid="statement-end" className="w-[150px] bg-white border-slate-200" /></div>
        <div className="space-y-1"><span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Canale</span>
          <Select value={workspace} onValueChange={setWorkspace}>
            <SelectTrigger className="w-[190px] bg-white border-slate-200" data-testid="statement-workspace"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti i canali</SelectItem>{channels.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="space-y-1"><span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Campagna</span>
          <Select value={campaign} onValueChange={setCampaign}>
            <SelectTrigger className="w-[230px] bg-white border-slate-200" data-testid="statement-campaign"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le campagne</SelectItem>
              {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{workspace === "all" ? ` · ${c.workspace_name}` : ""}{c.date ? ` (${dmy(c.date)})` : ""}</SelectItem>)}
            </SelectContent>
          </Select></div>
      </div>

      {loading && !data ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#1E4FD8]" /></div>
      ) : data && (
        <div className={`space-y-6 ${loading ? "opacity-60" : ""}`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="statement-totals">
            {[["Totale addebiti", eur(data.totals.cost), "statement-total-cost"], ["Messaggi addebitabili", data.totals.messages.toLocaleString("it-IT"), "statement-total-messages"],
              ["Costo medio / msg", data.totals.unit_cost != null ? eur(data.totals.unit_cost) : "—", "statement-unit-cost"], ["Periodo", `${dmy(start)} → ${dmy(end)}`, "statement-period"]].map(([l, v, id]) => (
              <div key={l} className="bg-white border border-slate-200 rounded-2xl p-4">
                <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">{l}</p>
                <p className="font-display text-xl font-extrabold text-slate-900 mt-1" data-testid={id}>{v}</p>
              </div>
            ))}
          </div>

          {data.sections.map((s) => (
            <div key={s.workspace.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`statement-section-${s.workspace.id}`}>
              <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.workspace.color }} />
                  <span className="font-semibold text-slate-900">{s.workspace.name}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.source === "meta" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid={`statement-source-${s.workspace.id}`}>
                    {s.source === "meta" ? "addebiti reali Meta" : s.source === "stima" ? "stima da listino" : "non disponibile"}
                  </span>
                </div>
                <span className="font-display font-bold text-slate-900">{eur(s.totals.cost)} · {s.totals.messages} msg</span>
              </div>
              {s.by_campaign.length === 0 ? (
                <p className="px-5 py-6 text-sm text-slate-500">Nessun addebito nel periodo.</p>
              ) : (
                <div className="grid lg:grid-cols-5">
                  <div className="lg:col-span-3 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-slate-500 border-b border-slate-100"><th className="px-5 py-2 font-medium">Campagna</th><th className="px-3 py-2 font-medium hidden sm:table-cell">Categoria</th><th className="px-3 py-2 font-medium text-right">Msg</th><th className="px-3 py-2 font-medium text-right hidden sm:table-cell">Unit.</th><th className="px-5 py-2 font-medium text-right">Costo</th></tr></thead>
                      <tbody>
                        {s.by_campaign.map((r) => (
                          <tr key={r.campaign_id} className="border-b border-slate-50" data-testid={`statement-row-${s.workspace.id}-${r.campaign_id}`}>
                            <td className="px-5 py-2 text-slate-800">{r.name}</td>
                            <td className="px-3 py-2 text-slate-500 hidden sm:table-cell">{r.category}</td>
                            <td className="px-3 py-2 text-right text-slate-700">{r.messages}</td>
                            <td className="px-3 py-2 text-right text-slate-500 hidden sm:table-cell">{r.unit_cost != null ? eur(r.unit_cost) : "—"}</td>
                            <td className="px-5 py-2 text-right font-semibold text-slate-900">{eur(r.cost)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="lg:col-span-2 border-t lg:border-t-0 lg:border-l border-slate-100 overflow-x-auto max-h-80 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-white"><tr className="text-left text-slate-500 border-b border-slate-100"><th className="px-4 py-2 font-medium">Giorno</th><th className="px-3 py-2 font-medium text-right">Msg</th><th className="px-4 py-2 font-medium text-right">Costo</th></tr></thead>
                      <tbody>{s.by_day.map((r) => (
                        <tr key={r.day} className="border-b border-slate-50"><td className="px-4 py-1.5 text-slate-700">{dmy(r.day)}</td><td className="px-3 py-1.5 text-right text-slate-600">{r.messages}</td><td className="px-4 py-1.5 text-right text-slate-800">{eur(r.cost)}</td></tr>
                      ))}</tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ))}
          <p className="text-xs text-slate-500">Gli addebiti reali Meta (pricing analytics della WABA) sono ripartiti sulle campagne in proporzione ai messaggi reali inviati nello stesso giorno; le voci non riconducibili a una campagna (risposte in chat, automazioni) compaiono come "Conversazioni di servizio / altro".</p>
        </div>
      )}
      {data && <MonthlyChart workspace={workspace} />}
      <BillingPanel />
    </section>
  );
}

function MonthlyChart({ workspace }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    setRows(null);
    api.get("/statements/meta/monthly", { params: { months: 6, workspace } }).then((r) => setRows(r.data.months)).catch(() => setRows([]));
  }, [workspace]);
  if (!rows) return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#1E4FD8]" /></div>;
  if (!rows.length) return null;
  const last = rows[rows.length - 1];
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="statement-monthly-chart">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
        <div>
          <h3 className="font-display font-bold text-slate-900 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#1E4FD8]" /> Confronto mese su mese</h3>
          <p className="text-xs text-slate-500 mt-1">Addebiti Meta degli ultimi 6 mesi{workspace !== "all" ? " per il canale selezionato" : ", tutti i canali"}. Il mese in corso è parziale.</p>
        </div>
        {last?.delta_pct != null && (
          <span data-testid="statement-monthly-delta" className={`text-xs font-semibold px-2.5 py-1 rounded-full ${last.delta_pct > 0 ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>
            {last.delta_pct > 0 ? "+" : ""}{last.delta_pct}% vs mese precedente
          </span>
        )}
      </div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v} €`} />
            <Tooltip cursor={{ fill: "#EFF6FF" }} formatter={(v, n) => (n === "cost" ? [eur(v), "Addebiti"] : [v, "Messaggi"])} labelStyle={{ color: "#0F172A" }} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
            <Bar dataKey="cost" radius={[8, 8, 0, 0]}>
              {rows.map((r, i) => <Cell key={r.month} fill={r.partial ? "#93C5FD" : i === rows.length - 1 ? "#1E4FD8" : "#3B82F6"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3">
        {rows.map((r) => (
          <div key={r.month} className="text-center" data-testid={`statement-month-${r.month}`}>
            <p className="text-[11px] text-slate-500">{r.label}{r.partial ? "*" : ""}</p>
            <p className="text-sm font-semibold text-slate-900">{eur(r.cost)}</p>
            {r.delta_pct != null && <p className={`text-[10px] ${r.delta_pct > 0 ? "text-rose-600" : "text-emerald-600"}`}>{r.delta_pct > 0 ? "+" : ""}{r.delta_pct}%</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function BillingPanel() {
  const [info, setInfo] = useState(null);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/statements/meta/billing").then((r) => { setInfo(r.data); setEdits(Object.fromEntries(r.data.items.map((i) => [i.id, i.billing_email]))); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!info) return null;
  const save = async (w) => {
    setBusy(`save-${w.id}`);
    try { await api.put(`/statements/meta/billing/${w.id}`, { billing_email: edits[w.id] || "", statement_enabled: w.statement_enabled }); toast.success("Indirizzo salvato"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const toggle = async (w) => {
    try { await api.put(`/statements/meta/billing/${w.id}`, { billing_email: edits[w.id] || "", statement_enabled: !w.statement_enabled }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const sendNow = async (w) => {
    setBusy(`send-${w.id}`);
    try { const { data } = await api.post("/statements/meta/send-now", null, { params: { workspace: w.id } }); toast.success(`Estratto conto ${data.month} inviato a ${data.to}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invio non riuscito"); } finally { setBusy(null); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4" data-testid="statement-billing-panel">
      <div>
        <h3 className="font-display font-bold text-slate-900 flex items-center gap-2"><Mail className="h-4 w-4 text-[#1E4FD8]" /> Invio automatico mensile</h3>
        <p className="text-xs text-slate-500 mt-1">{info.schedule}: per ogni canale viene inviato via email il PDF dell'estratto conto del mese precedente all'indirizzo di fatturazione indicato (vuoto = {info.default_email || "email admin"}).</p>
      </div>
      <div className="space-y-3">
        {info.items.map((w) => (
          <div key={w.id} className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 border border-slate-100 rounded-xl p-3" data-testid={`billing-row-${w.id}`}>
            <div className="flex items-center gap-2 md:w-48 shrink-0">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: w.color }} />
              <span className="text-sm font-medium text-slate-800 truncate">{w.name}</span>
            </div>
            <Input type="email" value={edits[w.id] ?? ""} onChange={(e) => setEdits({ ...edits, [w.id]: e.target.value })} placeholder={info.default_email || "fatturazione@azienda.it"}
                   data-testid={`billing-email-${w.id}`} className="bg-white border-slate-200 flex-1" />
            <div className="flex items-center gap-2 shrink-0">
              <Button size="sm" variant="outline" onClick={() => save(w)} disabled={busy === `save-${w.id}`} data-testid={`billing-save-${w.id}`} className="bg-white border-slate-200 text-slate-700">
                {busy === `save-${w.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Salva"}
              </Button>
              <Button size="sm" onClick={() => sendNow(w)} disabled={busy === `send-${w.id}`} data-testid={`billing-send-now-${w.id}`} className="bg-[#1E4FD8] hover:bg-[#1a44bb] text-white">
                {busy === `send-${w.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1" /> Invia ora</>}
              </Button>
              <button type="button" onClick={() => toggle(w)} data-testid={`billing-toggle-${w.id}`} title={w.statement_enabled ? "Invio automatico attivo" : "Invio automatico disattivato"}
                      className={`text-[11px] px-2 py-1 rounded-full border ${w.statement_enabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                {w.statement_enabled ? "Attivo" : "Disattivo"}
              </button>
            </div>
            {w.last_statement_month && <span className="text-[11px] text-slate-400 md:ml-2" data-testid={`billing-last-${w.id}`}>Ultimo: {w.last_statement_month}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
